/**
 * UserDashboard.js
 * Uses shared/designSystem.js + shared/LineItemsTable.jsx
 * All original logic preserved; only imports & styling unified.
 */
import React, { useState, useEffect, useRef, useContext, useCallback, useMemo } from "react";
import { useAuth }      from "../AuthProvider";
import { usePDF }       from 'react-to-pdf';
import { ItemsContext } from '../App';
import { generateCostSheetPdf } from '../pdfGenerator';
import { useMsal }      from '@azure/msal-react';
import { getApiToken }  from '../authUtils';
import { DateRange }    from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

// Shared design system
import {
  COLORS, FONT, FIELD_SX, SELECT_SX,
  PageShell, StickyFilterBar, SectionCard,
  TH_SX, DataRow,
  CurrencyDisplay, MarginBadge, MarginChip,
  OrderStatusChip, STATUS_COLORS, EmptyState,
  PAYMENT_TERM_OPTIONS, formatCurrency, fadeUp,
  INTERNAL_CATEGORY,
} from '../shared/DesignSystem';
import LineItemsTable, { isInternalSwitch } from '../shared/LineItemsTable';
import ProductSelectorModal from '../components/ProductSelectorModal'

import {
  Box, Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Grid, Typography, TableContainer, Table, TableHead, TableRow,
  TableCell, TableBody, Paper, FormControl, InputLabel, Select, MenuItem,
  CircularProgress, Alert, Chip, Card, CardContent, Badge, TablePagination,
  InputAdornment, Autocomplete, Collapse, useMediaQuery, useTheme,
  Drawer, Tooltip as MuiTooltip, Snackbar, LinearProgress,
} from '@mui/material';
import { styled, alpha, keyframes } from "@mui/system";

import ArrowForwardIcon        from '@mui/icons-material/ArrowForward';
import ArrowBackIcon           from '@mui/icons-material/ArrowBack';
import SearchIcon              from '@mui/icons-material/Search';
import CloseIcon               from '@mui/icons-material/Close';
import DeleteForeverIcon       from '@mui/icons-material/DeleteForever';
import MenuIcon                from '@mui/icons-material/Menu';
import CalendarTodayIcon       from '@mui/icons-material/CalendarToday';
import WarningAmberIcon        from '@mui/icons-material/WarningAmber';
import LocalShippingIcon       from '@mui/icons-material/LocalShipping';
import CheckCircleOutlineIcon  from '@mui/icons-material/CheckCircleOutline';
import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout';
import Inventory2Icon          from '@mui/icons-material/Inventory2';
import ReceiptIcon             from '@mui/icons-material/Receipt';
import BrushIcon               from '@mui/icons-material/Brush';
import SendIcon                from '@mui/icons-material/Send';
import EmailIcon               from '@mui/icons-material/Email';
import PrintIcon               from '@mui/icons-material/Print';
import SearchOffIcon           from '@mui/icons-material/SearchOff';
import ExpandMoreIcon          from '@mui/icons-material/ExpandMore';
import ExpandLessIcon          from '@mui/icons-material/ExpandLess';
import ViewColumnIcon          from '@mui/icons-material/ViewColumn';

import DeliveriesTable  from "../components/DeliveriesTable";
import CostSheetHistory from "../components/CostSheetHistory";
import { buildChangeLogs } from "../shared/costSheetChangeLog";
import UserSelect       from "../helpers/UserSelect";
import ExportAlldata    from '../components/ExportAlldata';
import ExportByDate     from '../components/ExportByDate';
import SearchInput from "../components/SearchInput";

const urlconfig = require('../config/urlconfig');

// ─── PP ranges ───────────────────────────────────────────────────────────────
// Values are sent to the server as pp:<value>; "a-b" means above a, up to and including b
const PP_RANGES = [
  { label: 'All',               value: '' },
  { label: '₹0 – ₹230',        value: '<=230' },
  { label: '₹231 – ₹500',      value: '230-500' },
  { label: '₹501 – ₹1,000',    value: '500-1000' },
  { label: '₹1,001 – ₹2,000',  value: '1000-2000' },
  { label: '₹2,001 – ₹5,000',  value: '2000-5000' },
  { label: '₹5,001 – ₹10,000', value: '5000-10000' },
  { label: '₹10,001 – ₹20,000',value: '10000-20000' },
  { label: '₹20,001 – ₹50,000',value: '20000-50000' },
];

// Local calendar date as YYYY-MM-DD (toISOString would shift it to the UTC day)
const toIsoDate = (d) => {
  const date = new Date(d);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

// ─── Local styled components ─────────────────────────────────────────────────

const KpiCard = styled(Paper)(({ theme }) => ({
  borderRadius: 18, padding: theme.spacing(2.5), background: '#fff',
  position: 'relative', overflow: 'hidden', cursor: 'pointer',
  transition: 'transform .18s, box-shadow .18s',
  animation: `${fadeUp} .4s ease both`,
  '&:hover': { transform: 'translateY(-3px)' },
  userSelect: 'none',
}));

const SectionWrap = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  [theme.breakpoints.down('sm')]: { padding: theme.spacing(2) },
}));

// ─── Column visibility toggle ─────────────────────────────────────────────────
const ALL_COLUMNS = [
  'Date', 'Creation Date', 'Cost Sheet For', 'Client Name', 'Sales Order No',
  'SCM SPOC', 'Sales SPOC', 'Details', 'Brand', 'Category', 'Subcategory',
  'Delivery Date', 'Margin', 'Total PP', 'Total SP', 'PO Number',
  'Created By', 'Approval Status', 'Order Status',
];

const ColumnToggleMenu = ({ visibleCols, setVisibleCols }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  return (
    <Box ref={ref} sx={{ position: 'relative' }}>
      <Button variant="outlined" size="small" startIcon={<ViewColumnIcon />} onClick={() => setOpen(o => !o)}
        sx={{ borderRadius: 2, fontSize: 12, fontWeight: 600, borderColor: COLORS.borderAlt, color: COLORS.textAlt }}>
        Columns ({visibleCols.length}/{ALL_COLUMNS.length})
      </Button>
      {open && (
        <Paper elevation={4} sx={{ position: 'absolute', top: '110%', right: 0, zIndex: 50, minWidth: 220, p: 1.5, borderRadius: 2, border: `1px solid ${COLORS.borderAlt}`, maxHeight: 340, overflowY: 'auto' }}>
          <Typography fontSize={11} fontWeight={700} color="text.secondary" sx={{ px: 0.5, pb: 0.5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Toggle Columns</Typography>
          {ALL_COLUMNS.map(col => (
            <Box key={col} component="label" sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 0.5, py: 0.6, cursor: 'pointer', borderRadius: 1, '&:hover': { background: '#f3f4f6' }, fontSize: 13 }}>
              <input type="checkbox" checked={visibleCols.includes(col)}
                onChange={() => setVisibleCols(prev => prev.includes(col) ? prev.filter(c => c !== col) : [...prev, col])}
                style={{ accentColor: COLORS.indigo }} />
              {col}
            </Box>
          ))}
        </Paper>
      )}
    </Box>
  );
};

// ─── Overdue accordion ────────────────────────────────────────────────────────
const OverdueAccordion = ({ itemsArray, currentDate }) => {
  const [openDate, setOpenDate] = useState(null);

  const validOverdue = itemsArray.filter(item => {
    const d = new Date(item.deliveryDate);
    return d.getFullYear() > 1971 && !isNaN(d.getTime()) && d < currentDate && item.orderStatus !== 'Delivered';
  });
  const invalidDateItems = itemsArray.filter(item => {
    const d = new Date(item.deliveryDate);
    return (isNaN(d.getTime()) || d.getFullYear() <= 1971) && item.orderStatus !== 'Delivered';
  });
  const grouped = validOverdue.reduce((acc, item) => {
    const key = new Date(item.deliveryDate).toLocaleDateString('en-GB');
    (acc[key] = acc[key] || []).push(item);
    return acc;
  }, {});
  const sorted = Object.entries(grouped).sort(([a], [b]) => {
    const toDate = s => new Date(s.split('/').reverse().join('-'));
    return toDate(a) - toDate(b);
  });
  const daysSince = (dateStr) => Math.floor((currentDate - new Date(dateStr.split('/').reverse().join('-'))) / 86400000);
  const urgencyColor = (days) => days > 180 ? COLORS.danger : days > 30 ? COLORS.warning : COLORS.amber;

  return (
    <Box>
      {invalidDateItems.length > 0 && (
        <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2, borderRadius: 2, border: `1px solid ${COLORS.amber}` }}>
          <Typography variant="body2" fontWeight={700}>
            Data Quality: {invalidDateItems.length} order{invalidDateItems.length !== 1 ? 's' : ''} have invalid delivery dates (stored as 1/1/1970)
          </Typography>
        </Alert>
      )}
      {sorted.map(([date, deliveries]) => {
        const days = daysSince(date);
        const color = urgencyColor(days);
        const isOpen = openDate === date;
        return (
          <Paper key={date} variant="outlined" sx={{ mb: 1, borderRadius: 2, overflow: 'hidden', borderColor: isOpen ? color : COLORS.borderAlt }}>
            <Box onClick={() => setOpenDate(p => p === date ? null : date)}
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: '12px 16px', cursor: 'pointer', background: isOpen ? `${color}08` : '#fff', '&:hover': { background: `${color}08` }, transition: 'background .15s' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
                <Typography fontWeight={700} fontSize={14}>{date}</Typography>
                <Typography fontSize={12} color="text.secondary">({deliveries.length} order{deliveries.length !== 1 ? 's' : ''})</Typography>
                <Chip label={`${days}d overdue`} size="small" sx={{ fontSize: 11, fontWeight: 700, color, background: `${color}18`, border: `1px solid ${color}33`, height: 20 }} />
              </Box>
              {isOpen ? <ExpandLessIcon fontSize="small" sx={{ color: COLORS.textFaint }} /> : <ExpandMoreIcon fontSize="small" sx={{ color: COLORS.textFaint }} />}
            </Box>
            <Collapse in={isOpen}>
              <Box sx={{ borderTop: `1px solid #f3f4f6`, background: '#fafafa', p: '8px 16px 12px' }}>
                {deliveries.map(d => (
                  <Box key={d.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1, borderBottom: `1px solid #f0f0f0` }}>
                    <Box>
                      <Typography fontWeight={600} fontSize={13}>{d.salesOrderNo}</Typography>
                      <Typography fontSize={12} color="text.secondary">{d.clientName} · {d.scmspoc}</Typography>
                    </Box>
                    <OrderStatusChip status={d.orderStatus} />
                  </Box>
                ))}
              </Box>
            </Collapse>
          </Paper>
        );
      })}
      {sorted.length === 0 && !invalidDateItems.length && (
        <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
          <Typography fontSize={36} mb={1}>🎉</Typography>
          <Typography fontWeight={700}>No overdue deliveries!</Typography>
        </Box>
      )}
    </Box>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const UserDashboard = () => {
  const { username } = useAuth();
  const { items, deliveryItems, loading, error, refreshItems, fetchAllItemsForDeliveryAnalytics, legalClients, legalVendors, refreshLegalClientsAndVendors } = useContext(ItemsContext);
  const { instance, accounts } = useMsal();
  const account = accounts?.[0] || null;
  const theme   = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [showModal,        setShowModal]        = useState(false);
  const [viewModal,        setViewModal]        = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [customVendorPaymentTerms, setCustomVendorPaymentTerms] = useState({});

  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [orderStatusFilter,    setOrderStatusFilter]    = useState('all');
  const [ppFilter,             setPpFilter]             = useState('');
  const [dateRange,            setDateRange]            = useState([{ startDate: null, endDate: null, key: 'selection' }]);
  const [DateModal,            setDateModal]            = useState(false);
  const [filterDrawer,         setFilterDrawer]         = useState(false);
  const [activeStatusCard,     setActiveStatusCard]     = useState(null);
  const [activeTab,            setActiveTab]            = useState('dashboard');
  const [qtyErrors,            setQtyErrors]            = useState({});
  const [catalogTargetIndex, setCatalogTargetIndex] = useState(null);
  const [productModalOpen, setProductModalOpen] = useState(false);

  const { targetRef } = usePDF();

  const [visibleCols, setVisibleCols] = useState([
    'Date', 'Creation Date', 'Cost Sheet For', 'Client Name', 'Sales Order No',
    'SCM SPOC', 'Sales SPOC', 'Details', 'Brand', 'Category', 'Subcategory',
    'Delivery Date', 'Margin', 'Total PP', 'Total SP', 'Order Status', 'Approval Status',
  ]);

  const itemsArray = Array.isArray(deliveryItems.content) ? deliveryItems.content : [];
  const tableRef   = useRef(null);
  const [deliveriesToShow, setDeliveriesToShow] = useState([]);
  const [tableTitle,       setTableTitle]       = useState('');
  const [tableColor,       setTableColor]       = useState('');
  const [startOffset,      setStartOffset]      = useState(0);
  const [direction,        setDirection]        = useState('next');
  const [page,             setPage]             = useState(0);
  const [rowsPerPage,      setRowsPerPage]      = useState(10);

  const [brands,                  setBrands]                  = useState([]);
  const [categories,              setCategories]              = useState([]);
  const [categorySubcategoryMap,  setCategorySubcategoryMap]  = useState({});
  const [catalogLoading,          setCatalogLoading]          = useState(false);

  const currentDate = new Date();
  currentDate.setHours(0, 0, 0, 0);

  const initialSelectedItemState = {
    _id: null,
    items: [{ details: '', customDetails: '', brand: null, category: null, subcategory: null, qty: '', gst: '', pp: '', nlc: '', sp: '', nsp: '', totalPp: '', totalSp: '', margin: '', marginPercent: '', vendorName: '', vendorPaymentTerms: '', poNumber: '' }],
    costsheetfor: '', createdBy: '', salesOrderNo: '', clientName: '', salesPOC: '', scmspoc: '', orderStatus: 'Pending',
    pending: '', approvedBy: [], remarks: '', date: null, deliveryDate: null, userEmail: '', location: '',
    clientPaymentTerms: '', costSheetRemarks: '',
    transportationGST:'0',transportationTotalPp: '0', transportationTotalSp: '0', customizationGST:'0', customizationTotalPp: '0', customizationTotalSp: '0',
    marketingGST:'0',marketingTotalPp: '0', marketingTotalSp: '0', totalPP: '0.00', totalSP: '0.00', totalMargin: '0.00', totalMarginPercent: '0.00',
    internalcategory:'', customRows: [],
    legalVendors:[], legalClients:[]
  };

  const [selectedItem,  setSelectedItem]  = useState(initialSelectedItemState);
  const [originalItem,  setOriginalItem]  = useState(null);
  const [originalCustomTerms, setOriginalCustomTerms] = useState({});


  useEffect(() => {
    if (deliveriesToShow.length > 0 && tableRef.current) tableRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [deliveriesToShow]);

  // Search, filters and page are kept as they are, so the table can be reloaded in place after a save or delete
  const tableQueryRef = useRef('');
  const reloadTable = () => refreshItems(tableQueryRef.current, page, rowsPerPage);
  const [toast, setToast] = useState('');

  useEffect(() => {
    const filters = [`createdBy:"${username}"`];
    if (debouncedSearchQuery) filters.push(debouncedSearchQuery);
    if (orderStatusFilter !== 'all') filters.push(`orderStatus:${orderStatusFilter}`);
    if (ppFilter) filters.push(`pp:${ppFilter}`);
    const q = filters.join(' ');

    // The table's date range and status card are filtered on the server, so matches from every
    // page come back and the total count is right. Delivery analytics keeps its own date filter.
    const tableFilters = [];
    const { startDate, endDate } = dateRange?.[0] || {};
    if (startDate && endDate) tableFilters.push(`from:${toIsoDate(startDate)}`, `to:${toIsoDate(endDate)}`);
    if (activeStatusCard && activeStatusCard !== 'overdue' && activeStatusCard !== 'upcoming') {
      tableFilters.push(`status:'${activeStatusCard}'`);
    }

    tableQueryRef.current = [q, ...tableFilters].join(' ');
    refreshItems(tableQueryRef.current, page, rowsPerPage);
  }, [debouncedSearchQuery, orderStatusFilter, ppFilter, dateRange, activeStatusCard, page, rowsPerPage, username, refreshItems]);

  // Deliveries are the full list, so they reload only when their own query changes, not on
  // every page, card or date change
  useEffect(() => {
    const filters = [`createdBy:"${username}"`];
    if (debouncedSearchQuery) filters.push(debouncedSearchQuery);
    if (orderStatusFilter !== 'all') filters.push(`orderStatus:${orderStatusFilter}`);
    if (ppFilter) filters.push(`pp:${ppFilter}`);
    fetchAllItemsForDeliveryAnalytics(filters.join(' '));
  }, [debouncedSearchQuery, orderStatusFilter, ppFilter, username, fetchAllItemsForDeliveryAnalytics]);

  // The full-page spinner is only for the first load; later reloads keep the page and show a bar
  const [hasLoaded, setHasLoaded] = useState(false);
  const sawLoadingRef = useRef(false);
  useEffect(() => {
    if (loading) sawLoadingRef.current = true;
    else if (sawLoadingRef.current) setHasLoaded(true);
  }, [loading]);

  // A delete (or an edit that drops a sheet out of the filter) can leave the current page past the end
  useEffect(() => {
    const total = items?.totalElements;
    if (page > 0 && total != null && page * rowsPerPage >= total) {
      setPage(Math.max(0, Math.ceil(total / rowsPerPage) - 1));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items?.totalElements]);

  useEffect(() => {
            refreshLegalClientsAndVendors();
        }, [refreshLegalClientsAndVendors]); 

  // ── KPI COUNTS ────────────────────────────────────────────────────────────
  const cardCounts = useMemo(() => {
    let overdue = 0, upcoming = 0, delivered = 0, posReleased = 0,
        materialInward = 0, purchaseInvoice = 0, branding = 0, dispatched = 0, invoiceSent = 0;
    const ms = currentDate.getTime();
    for (const item of itemsArray) {
      if (item.createdBy !== username) continue;
      const s = item.orderStatus;
      if (s === 'Delivered') delivered++;
      else if (s === "PO's Released to vendors") posReleased++;
      else if (s === 'Material Inward Done') materialInward++;
      else if (s === 'Purchase Invoice Received') purchaseInvoice++;
      else if (s === 'Branding Completed') branding++;
      else if (s === 'Material Dispatched') dispatched++;
      // else if (s === 'Invoice Sent to client') invoiceSent++;
      if (item.deliveryDate) {
        const diff = (new Date(item.deliveryDate).getTime() - ms) / 86400000;
        if (diff < 0 && s === 'Pending') overdue++;
        else if (diff >= 0 && diff < 5 && s !== 'Delivered') upcoming++;
      }
    }
    return { overdue, upcoming, delivered, posReleased, materialInward, purchaseInvoice, branding, dispatched, invoiceSent };
  }, [itemsArray, username]);

  // ── DELIVERY CARD HANDLERS ────────────────────────────────────────────────
  const getByOffset = (offset, dir) =>
    itemsArray.filter(item => {
      if (item.createdBy !== username) return false;
      const diff = (new Date(item.deliveryDate) - currentDate) / 86400000;
      return dir === 'next'
        ? diff >= offset && diff < offset + 5 && item.orderStatus !== 'Delivered'
        : diff <= -offset && diff > -(offset + 5) && item.orderStatus !== 'Delivered';
    });

  const handleCardClick = (key) => {
    if (activeStatusCard === key) { setActiveStatusCard(null); setPage(0); return; }
    setActiveStatusCard(key); setPage(0);
    if (key === 'overdue') showPastOverdue();
    else if (key === 'upcoming') { setDirection('next'); setStartOffset(0); setDeliveriesToShow(getByOffset(0, 'next')); setTableTitle('Upcoming Deliveries - Sales Orders'); setTableColor('green'); tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
    else { setDeliveriesToShow(itemsArray.filter(i => i.orderStatus === key && i.createdBy === username)); setTableTitle(`${key} - Sales Orders`); setTableColor('blue'); tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
  };

  const showPastOverdue = () => {
    if (tableTitle === 'Overdue Deliveries') return;
    setDeliveriesToShow(itemsArray.filter(i => i.createdBy === username && new Date(i.deliveryDate) < currentDate && i.orderStatus === 'Pending').sort((a, b) => new Date(a.deliveryDate) - new Date(b.deliveryDate)));
    setTableTitle('Overdue Deliveries'); setTableColor('red'); setDirection('past'); setStartOffset(0);
    tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleNext = () => { if (tableTitle === 'Overdue Deliveries') return; const n = startOffset + 5; setStartOffset(n); setDeliveriesToShow(getByOffset(n, direction)); };
  const handlePrev = () => { if (tableTitle === 'Overdue Deliveries') return; const n = Math.max(0, startOffset - 5); setStartOffset(n); setDeliveriesToShow(getByOffset(n, direction)); };

  const clearDateFilter = () => { setDateRange([{ startDate: null, endDate: null, key: 'selection' }]); setPage(0); setDateModal(false); };
  const handleCloseModal = () => { setShowModal(false); setViewModal(false); setSelectedItem(initialSelectedItemState); };

  // ── EDIT MODAL TOTALS ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!selectedItem || (!showModal && !viewModal)) return;
    const customPP = (selectedItem.customRows || []).reduce((s, r) => s + (parseFloat(r.customTotalPp) || 0), 0);
    const customSP = (selectedItem.customRows || []).reduce((s, r) => s + (parseFloat(r.customTotalSp) || 0), 0);
    const tPP = selectedItem.items.reduce((s, i) => s + (parseFloat(i.totalPp) || 0), 0) +
      (parseFloat(selectedItem.transportationTotalPp) || 0) + (parseFloat(selectedItem.customizationTotalPp) || 0) + (parseFloat(selectedItem.marketingTotalPp) || 0)
      + customPP
      ;
    const tSP = selectedItem.items.reduce((s, i) => s + (parseFloat(i.totalSp) || 0), 0) +
      (parseFloat(selectedItem.transportationTotalSp) || 0) + (parseFloat(selectedItem.customizationTotalSp) || 0) + (parseFloat(selectedItem.marketingTotalSp) || 0)
      + customSP;
    const mg = tSP - tPP;
    setSelectedItem(prev => ({ ...prev, totalPP: tPP.toFixed(2), totalSP: tSP.toFixed(2), totalMargin: mg.toFixed(2), totalMarginPercent: tPP !== 0 ? ((mg / tPP) * 100).toFixed(1) : '0.0' }));
  }, [selectedItem.items, selectedItem.transportationTotalPp, selectedItem.transportationTotalSp, selectedItem.customizationTotalPp, selectedItem.customizationTotalSp, selectedItem.marketingTotalPp, selectedItem.marketingTotalSp, showModal, viewModal, selectedItem.customRows]);

  // ── EDIT CLICK ────────────────────────────────────────────────────────────
  const handleEditClick = (item) => {
    const standardTerms = ["advance", "against-delivery", "10-days", "15-days", "30-days", "45-days", "60-days"];
    const customTerms = {};
    const cs = item.costSummary || {};
    const prepared = {
      ...initialSelectedItemState, ...item,
      transportationTotalPp: item.transportationTotalPp?.toString() || '0',
      transportationTotalSp: item.transportationTotalSp?.toString() || '0',
      transportationGST: cs.transportationGST?.toString() || '0',
      customizationTotalPp:  item.customizationTotalPp?.toString()  || '0',
      customizationTotalSp:  item.customizationTotalSp?.toString()  || '0',
      customizationGST: cs.customizationGST?.toString() || '0',
      marketingTotalPp:      item.marketingTotalPp?.toString()      || '0',
      marketingTotalSp:      item.marketingTotalSp?.toString()      || '0',
      marketingGST:  cs.marketingGST?.toString() || '0',
      customRows: item.customRows || [],  
      items: item.items.map((sub, i) => {
        const pp = parseFloat(sub.pp) || 0, sp = parseFloat(sub.sp) || 0;
        const gst = parseFloat(sub.gst) || 0, qty = parseInt(sub.qty) || 0;
        const nlc = pp * (1 + gst / 100), nsp = sp * (1 + gst / 100);
        const tPP = pp * qty, tSP = sp * qty, mg = tSP - tPP;
        let terms = sub.vendorPaymentTerms; 
        if (!standardTerms.includes(terms)) { customTerms[i] = terms; terms = 'custom'; }
        const totPPGST = nlc * qty;
        const totSPGST = nsp * qty;
        return { ...sub, qty: sub.qty?.toString() || '0', gst: sub.gst?.toString() || '0', pp: sub.pp?.toString() || '0', sp: sub.sp?.toString() || '0', nlc: nlc.toFixed(2), nsp: nsp.toFixed(2), totalPp: tPP.toFixed(2), totalSp: tSP.toFixed(2), margin: mg.toFixed(2), marginPercent: tPP !== 0 ? ((mg / tPP) * 100).toFixed(1) : '0.0', totPPGST: totPPGST.toFixed(2),totSPGST: totSPGST.toFixed(2), vendorPaymentTerms: terms, legalVendorId:
          (sub.legalVendorId == null && sub.vendorName)
            ? 'custom'
            : sub.legalVendorId,
          brand: sub.brand || null, category: sub.category || null, subcategory: sub.subcategory || null
        };
      }),
    };
    setCustomVendorPaymentTerms(customTerms);
    setOriginalCustomTerms(customTerms);
    setSelectedItem(prepared);
    setOriginalItem(JSON.parse(JSON.stringify(prepared)));
    setShowModal(true);
  };

  const handleViewCostSheet = (item) => {
    const cs = item.costSummary || {}
    setSelectedItem({
      ...item,
      transportationGST: cs.transportationGST?.toString() || '0',
      customizationGST:  cs.customizationGST?.toString()  || '0',
      marketingGST:      cs.marketingGST?.toString()       || '0',
      items: item.items.map(sub => {
        const pp = parseFloat(sub.pp) || 0, sp = parseFloat(sub.sp) || 0;
        const gst = parseFloat(sub.gst) || 0, qty = parseInt(sub.qty) || 0;
        const nlc = pp * (1 + gst / 100), nsp = sp * (1 + gst / 100);
        const tPP = pp * qty, tSP = sp * qty, mg = tSP - tPP;
        const totPPGST = nlc * qty;
        const totSPGST = nsp * qty; 
        return { ...sub, nlc: nlc.toFixed(2), nsp: nsp.toFixed(2), totalPp: tPP.toFixed(2), totalSp: tSP.toFixed(2), margin: mg.toFixed(2), marginPercent: tPP !== 0 ? ((mg / tPP) * 100).toFixed(1): '0.0', totPPGST: totPPGST.toFixed(2), totSPGST: totSPGST.toFixed(2)  };
      }),
    });
    setViewModal(true);
  };

  // ── AUTH ──────────────────────────────────────────────────────────────────
  const sendAuthenticatedRequest = useCallback(async (url, method, body) => {
    const token = await getApiToken(instance, account);
    const res = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) { const e = await res.json().catch(() => ({ message: res.statusText })); throw new Error(`API ${res.status}: ${e.message}`); }
    return res.headers.get('content-type')?.includes('application/json') ? res.json() : res.text();
  }, [instance, account]);

  // ── CATALOG DATA ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!account) return;
    (async () => {
      setCatalogLoading(true);
      try {
        const [brandsData, catsData] = await Promise.all([
          sendAuthenticatedRequest(`${urlconfig.backend_api_base_url}/api/v2/catalog/brands`, 'GET'),
          sendAuthenticatedRequest(`${urlconfig.backend_api_base_url}/api/v2/catalog/categories`, 'GET'),
        ]);
        setBrands(brandsData || []);
        setCategories(catsData || []);
        // Every category's sub-categories in one request, rather than one request per category
        const subsByCategory = await sendAuthenticatedRequest(`${urlconfig.backend_api_base_url}/api/v2/catalog/subcategories-by-category`, 'GET');
        const map = {};
        for (const cat of catsData) map[cat.id] = subsByCategory?.[cat.id] || [];
        setCategorySubcategoryMap(map);
      } catch (e) { console.error(e); }
      finally { setCatalogLoading(false); }
    })();
  }, [account, sendAuthenticatedRequest]);

  // ── DELETE ────────────────────────────────────────────────────────────────
  const handleDelete = async (item) => {
    if (!item?.id) return;
    if (!window.confirm(`Delete cost sheet ${item.salesOrderNo}?`)) return;
    try {
      await sendAuthenticatedRequest(`${urlconfig.backend_api_base_url}/api/v2/delete-costsheet/${item.id}`, 'DELETE');
      await sendAuthenticatedRequest(`${urlconfig.backend_api_base_url}/api/v2/send-email`, 'POST', {
        subject: `Cost Sheet Deleted: ${item.salesOrderNo}`,
        body: `Cost sheet ${item.salesOrderNo} deleted by ${username}.`,
        salesOrderNo: item.salesOrderNo, createdBy: item.createdBy, userEmail: item.userEmail,
      });
      reloadTable();
      setToast(`Cost sheet ${item.salesOrderNo} deleted`);
    } catch (e) { alert(`Error: ${e.message}`); }
  };

  // ── EDIT ITEM CHANGE ──────────────────────────────────────────────────────
  const handleTopLevelChange = (field, value) => {
    setSelectedItem(prev => ({
      ...prev, [field]: value,
      ...(field === 'pending' && value === 'false' ? { orderStatus: 'Pending' } : {}),
      // Moving into or out of Internal starts each line's product afresh
      ...(field === 'costsheetfor' && isInternalSwitch(prev.costsheetfor, value) ? { items: (prev.items || []).map(i => ({ ...i, details: '', customDetails: '', esin: '', ecomProductName: '', brand: null, category: null, subcategory: null })) } : {}),
    }));
  };

  const handleEditItemChange = (index, field, value) => {
    setSelectedItem(prev => ({
      ...prev,
      items: prev.items.map((item, idx) => {
        if (idx !== index) return item;
        const u = { ...item, [field]: value };
        if (field === 'category') u.subcategory = null;
        const pp = parseFloat(u.pp) || 0, sp = parseFloat(u.sp) || 0;
        const gst = parseFloat(u.gst) || 0, qty = parseInt(u.qty) || 0;
        const nlc = pp * (1 + gst / 100), nsp = sp * (1 + gst / 100);
        const tPP = pp * qty, tSP = sp * qty, mg = tSP - tPP;
        const totPPGST = nlc * qty;
        const totSPGST = nsp * qty;
        return { ...u, nlc: nlc.toFixed(2), nsp: nsp.toFixed(2), totalPp: tPP.toFixed(2), totalSp: tSP.toFixed(2), margin: mg.toFixed(2), marginPercent: tPP !== 0 ? ((mg / tPP) * 100).toFixed(1) : '0.0', totPPGST: totPPGST.toFixed(2), totSPGST: totSPGST.toFixed(2) };
      }),
    }));
  };

  const compareChanges = (original, current) => {
    const fields = ['clientName', 'salesPOC', 'scmspoc', 'clientPaymentTerms', 'location', 'transportationTotalPp', 'transportationTotalSp', 'customizationTotalPp', 'customizationTotalSp', 'marketingTotalPp', 'marketingTotalSp'];
    if (original.orderStatus === current.orderStatus) return false;
    for (const f of fields) { if (original[f] !== current[f]) return false; }
    if (original.items.length !== current.items.length) return false;
    for (let i = 0; i < current.items.length; i++) {
      const o = original.items[i];
      for (const k of ['details', 'customDetails', 'qty', 'gst', 'pp', 'sp', 'vendorName', 'vendorPaymentTerms', 'poNumber', 'brand', 'category', 'subcategory']) {
        const ov = k === 'vendorPaymentTerms' && o[k] === 'custom' ? customVendorPaymentTerms[i] : (['brand','category','subcategory'].includes(k) ? o[k]?.id : o[k]);
        const cv = k === 'vendorPaymentTerms' && current.items[i][k] === 'custom' ? customVendorPaymentTerms[i] : (['brand','category','subcategory'].includes(k) ? current.items[i][k]?.id : current.items[i][k]);
        if (ov !== cv) return false;
      }
    }
    return true;
  };

  // Totals are recomputed by the effect above as soon as the modal opens, so they are
  // left out — otherwise the form looks edited before the user touches anything.
  const withoutDerivedTotals = (o) => {
    if (!o) return o;
    const { totalPP, totalSP, totalMargin, totalMarginPercent, ...rest } = o;
    return rest;
  };
  const hasChanges = () =>
    JSON.stringify(withoutDerivedTotals(originalItem)) !== JSON.stringify(withoutDerivedTotals(selectedItem)) ||
    JSON.stringify(originalCustomTerms) !== JSON.stringify(customVendorPaymentTerms);
  const handleSaveEdit = () => { if (hasChanges()) setShowConfirmModal(true); else setShowModal(false); };
  const isInternal = selectedItem?.costsheetfor?.toLowerCase() === 'internal';

  const confirmSaveChanges = async () => {
    setShowConfirmModal(false);
    const onlyStatus = compareChanges(originalItem, selectedItem);
    try {
      const itemsPayload = selectedItem.items.map((item, i) => ({
        ...item,
        qty: parseInt(item.qty) || 0, gst: parseFloat(item.gst) || 0, pp: parseFloat(item.pp) || 0, sp: parseFloat(item.sp) || 0,
        marginPercent: parseFloat(item.marginPercent) || 0,
        legalVendorId: item.legalVendorId === 'custom' ? null : item.legalVendorId,
        vendorPaymentTerms: item.vendorPaymentTerms === 'custom' ? customVendorPaymentTerms[i] : item.vendorPaymentTerms,
        brand:       item.brand       ? { id: item.brand.id }       : null,
        category:    item.category    ? { id: item.category.id }    : null,
        subcategory: item.subcategory ? { id: item.subcategory.id } : null,
      }));
      const costsheetForChanged = originalItem.costsheetfor !== selectedItem.costsheetfor;
      const internalCategoryChanged = originalItem.internalCategory !== selectedItem.internalCategory;

      const payload = {
        ...selectedItem, items: itemsPayload,
        transportationTotalPp: parseFloat(selectedItem.transportationTotalPp) || 0, transportationTotalSp: parseFloat(selectedItem.transportationTotalSp) || 0,
        customizationTotalPp: parseFloat(selectedItem.customizationTotalPp) || 0, customizationTotalSp: parseFloat(selectedItem.customizationTotalSp) || 0,
        marketingTotalPp: parseFloat(selectedItem.marketingTotalPp) || 0, marketingTotalSp: parseFloat(selectedItem.marketingTotalSp) || 0,
        customRows: selectedItem.customRows || [], 
        costSummary: {
        transportationGST: parseFloat(selectedItem.transportationGST) || 0,
        customizationGST:  parseFloat(selectedItem.customizationGST)  || 0,
        marketingGST:      parseFloat(selectedItem.marketingGST)      || 0,
      },
        ...(originalItem.pending === 'true' && !onlyStatus && !costsheetForChanged && !internalCategoryChanged ? { pending: 'undefined', approvedBy: [], remarks: '' } : {}),
      };

      await sendAuthenticatedRequest(`${urlconfig.backend_api_base_url}/api/v2/edit-costsheet/${selectedItem.id}`, 'PUT', payload);

      // Change log — only fields the user actually changed
      const changes = buildChangeLogs({
        original: originalItem,
        edited: selectedItem,
        resolveTerms: (item, i, which) => item.vendorPaymentTerms === 'custom'
          ? (which === 'old' ? originalCustomTerms : customVendorPaymentTerms)[i]
          : item.vendorPaymentTerms,
        changedBy: username,
      });
      if (changes.length > 0) await sendAuthenticatedRequest(`${urlconfig.backend_api_base_url}/api/v2/logs/item/${selectedItem.id}`, 'POST', { logs: changes });
      // const costsheetForChanged = originalItem.costsheetfor !== selectedItem.costsheetfor;
      if (!onlyStatus && !costsheetForChanged && !internalCategoryChanged) {
        await sendAuthenticatedRequest(`${urlconfig.backend_api_base_url}/api/v2/send-email`, 'POST', {
          subject: `Cost Sheet Edited: ${selectedItem.salesOrderNo}`,
          body: `Cost sheet ${selectedItem.salesOrderNo} was edited by ${username}.`,
          salesOrderNo: selectedItem.salesOrderNo, createdBy: selectedItem.createdBy, userEmail: selectedItem.userEmail,
        });
      }
      await reloadTable();
      setShowModal(false);
      setToast(`Cost sheet ${selectedItem.salesOrderNo} saved`);
    } catch (e) { alert(`Failed: ${e.message}`); }
  };

  const addItem    = () => setSelectedItem(prev => ({ ...prev, items: [...prev.items, { ...initialSelectedItemState.items[0] }] }));
  const removeItem = (i) => {
    if (selectedItem.items.length <= 1) { alert('At least one item is required.'); return; }
    setSelectedItem(prev => ({ ...prev, items: prev.items.filter((_, idx) => idx !== i) }));
  };

  // Every filter is applied by the server, so this is the current page of matching cost sheets
  const filteredContent = items.content || [];

 const aggPP = filteredContent.reduce((a, i) => {
    const customPP = (i.customRows || []).reduce((s, r) => s + (parseFloat(r.customTotalPp) || 0), 0);
    return a + (i.items?.reduce((s, x) => s + x.qty * x.pp, 0) || 0) + (i.transportationTotalPp || 0) + (i.customizationTotalPp || 0) + (i.marketingTotalPp || 0) + customPP;
  }, 0);

  const aggSP = filteredContent.reduce((a, i) => {
    const customSP = (i.customRows || []).reduce((s, r) => s + (parseFloat(r.customTotalSp) || 0), 0);
    return a + (i.items?.reduce((s, x) => s + x.qty * x.sp, 0) || 0) + (i.transportationTotalSp || 0) + (i.customizationTotalSp || 0) + (i.marketingTotalSp || 0) + customSP;
  }, 0);
  const aggMg  = aggSP - aggPP;
  const aggPct = aggPP > 0 ? ((aggMg / aggPP) * 100).toFixed(1) : '0.0';

  const anyFilter = dateRange?.[0]?.startDate || debouncedSearchQuery.trim() || orderStatusFilter !== 'all' || ppFilter || activeStatusCard;
  const clearFilters = () => { setDateRange([{ startDate: null, endDate: null, key: 'selection' }]); setDebouncedSearchQuery(''); setOrderStatusFilter('all'); setPpFilter(''); setActiveStatusCard(null); setPage(0); };

  // ── KPI DATA ──────────────────────────────────────────────────────────────
  const kpiData = [
    { key: 'overdue',                    label: 'Overdue Deliveries',  count: cardCounts.overdue,        icon: <WarningAmberIcon />,          colorHex: '#dc2626', bg: 'linear-gradient(135deg,#dc2626,#f87171)' },
    { key: 'upcoming',                   label: 'Next 5 Days',         count: cardCounts.upcoming,       icon: <LocalShippingIcon />,         colorHex: '#15803d', bg: 'linear-gradient(135deg,#15803d,#4ade80)' },
    { key: 'Delivered',                  label: 'Delivered',           count: cardCounts.delivered,      icon: <CheckCircleOutlineIcon />,    colorHex: '#0284c7', bg: 'linear-gradient(135deg,#0284c7,#38bdf8)' },
    { key: "PO's Released to vendors",   label: "PO's Released",       count: cardCounts.posReleased,    icon: <ShoppingCartCheckoutIcon />,  colorHex: '#65a30d', bg: 'linear-gradient(135deg,#65a30d,#a3e635)' },
    { key: 'Material Inward Done',       label: 'Material Inward',     count: cardCounts.materialInward, icon: <Inventory2Icon />,            colorHex: '#ea580c', bg: 'linear-gradient(135deg,#ea580c,#facc15)' },
    { key: 'Purchase Invoice Received',  label: 'Purchase Invoice',    count: cardCounts.purchaseInvoice,icon: <ReceiptIcon />,               colorHex: '#4b5563', bg: 'linear-gradient(135deg,#4b5563,#9ca3af)' },
    { key: 'Branding Completed',         label: 'Branding Completed',  count: cardCounts.branding,       icon: <BrushIcon />,                 colorHex: '#4f46e5', bg: 'linear-gradient(135deg,#4f46e5,#818cf8)' },
    { key: 'Material Dispatched',        label: 'Material Dispatched', count: cardCounts.dispatched,     icon: <SendIcon />,                  colorHex: '#e11d48', bg: 'linear-gradient(135deg,#e11d48,#fb7185)' },
    // { key: 'Invoice Sent to client',     label: 'Invoice Sent',        count: cardCounts.invoiceSent,    icon: <EmailIcon />,                 colorHex: '#0d9488', bg: 'linear-gradient(135deg,#0d9488,#2dd4bf)' },
  ];

  // ── COLUMN MAP ────────────────────────────────────────────────────────────
  const COL_KEY_MAP = {
    'Date':           i => <TableCell key="Date">{new Date(i.date).toLocaleDateString('en-GB')}</TableCell>,
    'Creation Date':  i => <TableCell key="CreationDate">{i.createdAT ? new Date(i.createdAT).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : 'N/A'}</TableCell>,
    'Cost Sheet For': i => <TableCell key="CSF">{i.costsheetfor}</TableCell>,
    'Client Name':    i => <TableCell key="Client" sx={{ fontWeight: 600 }}>{i.clientName}</TableCell>,
    'Sales Order No': i => <TableCell key="SO" sx={{ fontFamily: FONT.mono, fontSize: 12, color: COLORS.indigo }}>{i.salesOrderNo}</TableCell>,
    'SCM SPOC':       i => <TableCell key="SCM">{i.scmspoc}</TableCell>,
    'Sales SPOC':     i => <TableCell key="SPOC">{i.salesPOC}</TableCell>,
    'Details':        i => <TableCell key="Details">{i.items?.map(x => x.details).join(', ')}</TableCell>,
    'Brand':          i => <TableCell key="Brand">{i.items.map(x => x.brand?.name || 'N/A').join(', ')}</TableCell>,
    'Category':       i => <TableCell key="Cat">{i.items.map(x => x.category?.name || 'N/A').join(', ')}</TableCell>,
    'Subcategory':    i => <TableCell key="Sub">{i.items.map(x => x.subcategory?.name || 'N/A').join(', ')}</TableCell>,
    'Delivery Date':  i => {
      const d = new Date(i.deliveryDate);
      const invalid = isNaN(d.getTime()) || d.getFullYear() <= 1971;
      const diff = (d - currentDate) / 86400000;
      return (
        <TableCell key="Del">
          <MuiTooltip title={invalid ? '⚠ Invalid date' : diff < 0 ? `${Math.abs(Math.floor(diff))}d overdue` : `${Math.floor(diff)}d away`} arrow>
            <Typography sx={{ bgcolor: invalid ? '#fef2f2' : diff < 0 ? alpha(COLORS.danger, 0.08) : 'transparent', color: invalid ? COLORS.danger : 'text.primary', p: '2px 8px', borderRadius: 1, fontSize: 13, fontWeight: invalid ? 700 : 400, display: 'inline-block' }}>
              {invalid ? '⚠ Invalid' : d.toLocaleDateString('en-GB')}
            </Typography>
          </MuiTooltip>
        </TableCell>
      );
    },
    'Margin': i => {
      const customPP = (i.customRows || []).reduce((s, r) => s + (parseFloat(r.customTotalPp) || 0), 0);
      const customSP = (i.customRows || []).reduce((s, r) => s + (parseFloat(r.customTotalSp) || 0), 0);
      const pp = i.items?.reduce((a, s) => a + s.qty * s.pp, 0) + (i.transportationTotalPp || 0) + (i.customizationTotalPp || 0) + (i.marketingTotalPp || 0) + customPP;
      const sp = i.items?.reduce((a, s) => a + s.qty * s.sp, 0) + (i.transportationTotalSp || 0) + (i.customizationTotalSp || 0) + (i.marketingTotalSp || 0) + customSP;
      return <TableCell key="Margin" sx={{ fontFamily: FONT.mono, fontWeight: 700, color: sp - pp >= 0 ? COLORS.success : COLORS.danger }}>₹{formatCurrency(sp - pp)}</TableCell>;
    },
    'Total PP': i => {
      const customPP = (i.customRows || []).reduce((s, r) => s + (parseFloat(r.customTotalPp) || 0), 0);
      return <TableCell key="TotalPP" sx={{ fontFamily: FONT.mono, fontSize: 12 }}>₹{formatCurrency(i.items?.reduce((a, s) => a + s.qty * s.pp, 0) + (i.transportationTotalPp || 0) + (i.customizationTotalPp || 0) + (i.marketingTotalPp || 0) + customPP)}</TableCell>;
    },
    'Total SP': i => {
      const customSP = (i.customRows || []).reduce((s, r) => s + (parseFloat(r.customTotalSp) || 0), 0);
      return <TableCell key="TotalSP" sx={{ fontFamily: FONT.mono, fontSize: 12 }}>₹{formatCurrency(i.items?.reduce((a, s) => a + s.qty * s.sp, 0) + (i.transportationTotalSp || 0) + (i.customizationTotalSp || 0) + (i.marketingTotalSp || 0) + customSP)}</TableCell>;
    },

    'PO Number': i => <TableCell key="PO">{i.items.map(x => x.poNumber).join(', ')}</TableCell>,
    'Created By': i => <TableCell key="CreatedBy">{i.createdBy}</TableCell>,
    'Approval Status': i => (
      <TableCell key="Approval">
        {i.pending === 'true' && i.approvedBy?.length > 0
          ? <Typography variant="caption" sx={{ color: COLORS.success, fontWeight: 700 }}>✓ {i.approvedBy.join(', ')}</Typography>
          : i.pending === 'false'
            ? <Box><Typography variant="caption" color="error" display="block">✗ Rejected by {i.approvedBy?.join(', ')}</Typography><Typography variant="caption" sx={{ color: 'error.main', fontWeight: 700 }}>Note: {i.remarks}</Typography></Box>
            : <Typography variant="caption" sx={{ color: COLORS.amber, fontWeight: 700 }}>⏳ Pending</Typography>
        }
      </TableCell>
    ),
    'Order Status': i => <TableCell key="Status"><OrderStatusChip status={i.orderStatus} /></TableCell>,
  };

  // ── FILTER CONTROLS ───────────────────────────────────────────────────────
  const FilterControls =  useMemo(() => (
    <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 2, alignItems: isMobile ? 'stretch' : 'center', flexWrap: 'wrap' }}>
         <SearchInput
              placeholder="Search orders, clients, SPOC..."
              onSearch={(val) => {
                setDebouncedSearchQuery(val);
                setPage(0);
              }}
              onClear={() => {
                setDebouncedSearchQuery('');
                setPage(0);
              }}
            />

      <FormControl size="small" sx={{ minWidth: 160, ...FIELD_SX }}>
        <InputLabel>PP Filter</InputLabel>
        <Select value={ppFilter} label="PP Filter" onChange={(e) => { setPpFilter(e.target.value); setPage(0); }} sx={SELECT_SX}>
          {PP_RANGES.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ minWidth: 160, ...FIELD_SX }}>
        <InputLabel>Order Status</InputLabel>
        <Select value={orderStatusFilter} label="Order Status" onChange={(e) => { setOrderStatusFilter(e.target.value); setPage(0); }} sx={SELECT_SX}>
          <MenuItem value="all">All Statuses</MenuItem>
          <MenuItem value="Pending">Pending</MenuItem>
          <MenuItem value="on-hold">On Hold</MenuItem>
          <MenuItem value="delivered">Delivered</MenuItem>
        </Select>
      </FormControl>
      <Badge badgeContent={dateRange[0].startDate ? 1 : 0} color="primary">
        <Button variant="outlined" size="medium" startIcon={<CalendarTodayIcon fontSize="small" />} onClick={() => setDateModal(true)}
          sx={{ textTransform: 'none', color: dateRange[0].startDate ? COLORS.indigo : 'text.secondary', borderColor: dateRange[0].startDate ? COLORS.indigo : COLORS.borderAlt, borderRadius: 2 }}>
          {dateRange[0].startDate ? `${dateRange[0].startDate.toLocaleDateString()} — ${dateRange[0].endDate?.toLocaleDateString()}` : 'Date Range'}
        </Button>
      </Badge>
      {anyFilter && (
        <Button size="small" startIcon={<DeleteForeverIcon />} onClick={clearFilters} color="warning" variant="outlined"
          sx={{ textTransform: 'none', borderRadius: 2 }}>
          Clear Filters
        </Button>
      )}
      <Box sx={{ ml: isMobile ? 0 : 'auto', display: 'flex', gap: 1 }}>
        <ExportAlldata />
        <ExportByDate />
      </Box>
    </Box>
  ));

  if (loading && !hasLoaded) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', gap: 2 }}>
      <CircularProgress size={48} thickness={3} sx={{ color: COLORS.indigo }} />
      <Typography color="text.secondary" fontWeight={600} fontFamily={FONT.display}>Loading…</Typography>
    </Box>
  );
  if (error) return <Alert severity="error" sx={{ m: 4, borderRadius: 3 }}>{error}</Alert>;

   const isEditFormValid = () => {
    if (!selectedItem.clientName?.trim()) return false;
    if (!selectedItem.costsheetfor?.trim()) return false;
    if (!selectedItem.salesPOC?.trim()) return false;
    if (!selectedItem.scmspoc?.trim()) return false;
    if (!selectedItem.location?.trim()) return false;
    if (!selectedItem.orderStatus?.trim()) return false;

    // Every line item must be filled in
    if (!selectedItem.items || selectedItem.items.length === 0) return false;
    return selectedItem.items.every(item => {
      const hasQty = item.qty !== '' && item.qty != null && parseFloat(item.qty) > 0;
      const hasPP = item.pp !== '' && item.pp != null && parseFloat(item.pp) >= 0;
      const hasSP = item.sp !== '' && item.sp != null && parseFloat(item.sp) >= 0;
      const hasVendorName = item.legalVendorId && item.legalVendorId !== 'custom' ? true : (item.vendorName?.trim() !== '' && item.vendorName != null);
      const hasDetails = item.details?.trim();
      return hasQty && hasPP && hasSP && hasDetails && hasVendorName;
    });
  };

  // ─────────────────────────────────────────────────────────────────────────

  return (
    <PageShell>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=DM+Mono:wght@400;500&display=swap');`}</style>

      {/* ── HEADER ── */}
      <Box sx={{ bgcolor: '#fff', borderBottom: `1px solid ${COLORS.borderAlt}`, px: { xs: 2, md: 4 }, py: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        {isMobile && (
          <IconButton size="small" onClick={() => setFilterDrawer(true)}>
            <Badge badgeContent={[orderStatusFilter !== 'all', !!dateRange[0]?.startDate, !!ppFilter, !!activeStatusCard].filter(Boolean).length} color="error">
              <MenuIcon />
            </Badge>
          </IconButton>
        )}
        <Box sx={{ flex: 1 }}>
          <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight={900} letterSpacing="-0.5px" fontFamily={FONT.display}>
            User Dashboard
          </Typography>
          <Typography variant="caption" color="text.secondary" fontWeight={500}>
            {items.totalElements || 0} records for {username}
          </Typography>
        </Box>
      </Box>

      {/* Desktop sticky filter */}
      {!isMobile && <StickyFilterBar elevation={0}>{FilterControls}</StickyFilterBar>}

      {/* Mobile drawer */}
      <Drawer anchor="left" open={filterDrawer} onClose={() => setFilterDrawer(false)}>
        <Box sx={{ width: 320, p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography fontWeight={800} variant="h6" fontFamily={FONT.display}>Filters & Exports</Typography>
            <IconButton size="small" onClick={() => setFilterDrawer(false)}><CloseIcon /></IconButton>
          </Box>
          <FilterControls />
        </Box>
      </Drawer>

      {/* Date modal */}
      <Dialog open={DateModal} onClose={() => setDateModal(false)} maxWidth="sm">
        <DialogTitle fontWeight={800}>Select Date Range</DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <DateRange editableDateInputs onChange={i => { setDateRange([i.selection]); setPage(0); }} moveRangeOnFirstSelection={false} ranges={dateRange} />
        </DialogContent>
        <DialogActions>
          <Button onClick={clearDateFilter} color="secondary">Clear</Button>
          <Button variant="contained" onClick={() => setDateModal(false)} sx={{ bgcolor: COLORS.indigo }}>Apply</Button>
        </DialogActions>
      </Dialog>

      <SectionWrap>
        {/* ── TABS ── */}
        <Box sx={{ display: 'flex', gap: 1, mb: 3, borderBottom: `2px solid ${COLORS.borderAlt}`, pb: 0 }}>
          {[['dashboard', '⊞ Dashboard'], ['overdue', '⚠ Overdue View']].map(([tab, label]) => (
            <Button key={tab} onClick={() => setActiveTab(tab)} disableRipple
              sx={{ borderRadius: '8px 8px 0 0', px: 2.5, py: 1, fontSize: 13, fontWeight: 700, fontFamily: FONT.display,
                color: activeTab === tab ? COLORS.indigo : '#6b7280',
                background: activeTab === tab ? COLORS.indigoBg : 'transparent',
                borderBottom: activeTab === tab ? `2px solid ${COLORS.indigo}` : '2px solid transparent',
                mb: '-2px', '&:hover': { background: '#f3f4f6' } }}>
              {label}
            </Button>
          ))}
        </Box>

        {activeTab === 'overdue' ? (
          <Box>
            <Typography variant="h6" fontWeight={700} mb={0.5} fontFamily={FONT.display}>Your Overdue Deliveries</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>All orders past their delivery date, grouped by date. Invalid dates indicate a data entry issue.</Typography>
            <OverdueAccordion itemsArray={itemsArray.filter(i => i.createdBy === username)} currentDate={currentDate} />
          </Box>
        ) : (
          <>
            {/* ── KPI CARDS ── */}
            <Grid container spacing={2} sx={{ mb: 4 }}>
              {kpiData.map((c, i) => {
                const isActive = activeStatusCard === c.key;
                return (
                  <Grid item xs={6} sm={4} md={3} lg={2.4} key={c.key}>
                    <KpiCard onClick={() => handleCardClick(c.key)}
                      sx={{ animationDelay: `${i * 60}ms`, border: `2px solid ${isActive ? c.colorHex : 'transparent'}`, boxShadow: isActive ? `0 8px 24px ${c.colorHex}40` : '0 2px 16px rgba(0,0,0,0.06)', transform: isActive ? 'translateY(-3px)' : 'none' }}>
                      <Box sx={{ position: 'absolute', right: -12, top: -12, width: 72, height: 72, borderRadius: '50%', background: c.bg, opacity: 0.12 }} />
                      <Typography sx={{ fontSize: 26, lineHeight: 1, mb: 0.5, color: c.colorHex }}>{c.icon}</Typography>
                      <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.1, letterSpacing: '-0.5px', fontFamily: FONT.display }}>{c.count}</Typography>
                      <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mt: 0.5 }}>{c.label}</Typography>
                      <Typography variant="caption" sx={{ color: isActive ? c.colorHex : COLORS.textFaint, fontSize: '0.68rem', display: 'block', mt: 0.5 }}>
                        {isActive ? 'Filtering table ↓' : 'Click to filter'}
                      </Typography>
                    </KpiCard>
                  </Grid>
                );
              })}
            </Grid>

            {loading && <LinearProgress sx={{ mb: 1.5, borderRadius: 1 }} />}

            {/* ── AGGREGATE BAR ── */}
            <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
              {[
                { label: 'Filtered Orders',             value: (items.totalElements || 0).toLocaleString(),            mono: false },
                { label: 'Total PP (this page)',        value: `₹${formatCurrency(aggPP)}`,                              mono: true  },
                { label: 'Total SP (this page)',        value: `₹${formatCurrency(aggSP)}`,                              mono: true  },
                { label: 'Margin (this page)',          value: `₹${formatCurrency(aggMg)} (${aggPct}%)`, positive: aggMg >= 0 },
              ].map(({ label, value, mono, positive }) => (
                <Grid item xs={12} sm={6} md={3} key={label}>
                  <Paper variant="outlined" sx={{ p: '12px 16px', borderRadius: 2, borderColor: '#e5e7eb' }}>
                    <Typography fontSize={10} fontWeight={700} color="text.secondary" textTransform="uppercase" letterSpacing="0.06em" mb={0.25}>
                      {label}
                    </Typography>
                    <Typography fontSize={20} fontWeight={800} sx={{
                      fontFamily: mono ? "'DM Mono', monospace" : 'inherit',
                      color: positive === false ? 'error.main' : positive ? '#22c55e' : '#111827',
                    }}>
                      {value}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            {/* Active filter chips */}
            {(activeStatusCard || ppFilter || orderStatusFilter !== 'all') && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                {activeStatusCard && <Chip label={`Card: ${kpiData.find(c => c.key === activeStatusCard)?.label || activeStatusCard}`} onDelete={() => { setActiveStatusCard(null); setPage(0); }} size="small" sx={{ fontSize: 11, fontWeight: 600, background: COLORS.indigoBg, color: COLORS.indigo }} />}
                {ppFilter && <Chip label={`PP: ${PP_RANGES.find(r => r.value === ppFilter)?.label || ppFilter}`} onDelete={() => setPpFilter('')} size="small" sx={{ fontSize: 11, fontWeight: 600, background: '#fff7ed', color: COLORS.warning }} />}
                {orderStatusFilter !== 'all' && <Chip label={`Status: ${orderStatusFilter}`} onDelete={() => setOrderStatusFilter('all')} size="small" sx={{ fontSize: 11, fontWeight: 600, background: '#f0fdf4', color: COLORS.success }} />}
              </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
              <ColumnToggleMenu visibleCols={visibleCols} setVisibleCols={setVisibleCols} />
            </Box>

            {/* ── MAIN TABLE ── */}
            <TableContainer component={Paper} elevation={0} sx={{ overflowX: 'auto', mb: 4, borderRadius: 3, border: `1px solid ${COLORS.borderAlt}` }}>
              <Table sx={{ minWidth: 900 }} ref={tableRef}>
                <TableHead>
                  <TableRow sx={{ background: COLORS.surface }}>
                    <TableCell sx={{ display: 'none' }}>Transportation total PP</TableCell>
                    <TableCell sx={{ display: 'none' }}>Transportation total SP</TableCell>
                    <TableCell sx={{ display: 'none' }}>Customization total PP</TableCell>
                    <TableCell sx={{ display: 'none' }}>Customization total SP</TableCell>
                    <TableCell sx={{ display: 'none' }}>Marketing total PP</TableCell>
                    <TableCell sx={{ display: 'none' }}>Marketing total SP</TableCell>
                    {visibleCols.map(col => <TableCell key={col} sx={TH_SX}>{col}</TableCell>)}
                    <TableCell sx={TH_SX}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.content && items.content.length > 0 ? (
                    filteredContent.map((item, rowIdx) => (
                      <DataRow key={item.id} rowindex={rowIdx} onClick={() => handleViewCostSheet(item)}>
                        <TableCell sx={{ display: 'none' }}>{item.transportationTotalPp}</TableCell>
                        <TableCell sx={{ display: 'none' }}>{item.transportationTotalSp}</TableCell>
                        <TableCell sx={{ display: 'none' }}>{item.customizationTotalPp}</TableCell>
                        <TableCell sx={{ display: 'none' }}>{item.customizationTotalSp}</TableCell>
                        <TableCell sx={{ display: 'none' }}>{item.marketingTotalPp}</TableCell>
                        <TableCell sx={{ display: 'none' }}>{item.marketingTotalSp}</TableCell>
                        {visibleCols.map(col => COL_KEY_MAP[col]?.(item))}
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Button variant="contained" size="small" onClick={() => handleEditClick(item)}
                              sx={{ borderRadius: 1.5, fontSize: 12, textTransform: 'none', bgcolor: COLORS.indigo, '&:hover': { bgcolor: COLORS.navy } }}>
                              Edit
                            </Button>
                            <Button variant="outlined" color="error" size="small" onClick={() => handleDelete(item)}
                              sx={{ borderRadius: 1.5, fontSize: 12, textTransform: 'none' }}>
                              Delete
                            </Button>
                          </Box>
                        </TableCell>
                      </DataRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={visibleCols.length + 1} sx={{ py: 6 }}>
                        <EmptyState title="No cost sheets found" message="Try adjusting your search or filters." />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={items.totalElements || 0}
                page={page} onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${count !== -1 ? count.toLocaleString() : `more than ${to}`}`}
                sx={{ borderTop: `1px solid ${COLORS.borderAlt}` }}
              />
            </TableContainer>
          </>
        )}
      </SectionWrap>

      {/* ── VIEW COST SHEET DIALOG ── */}
      <Dialog open={viewModal} onClose={handleCloseModal} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: 3, maxHeight: '90vh' } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1, borderBottom: `1px solid ${COLORS.border}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <ReceiptIcon sx={{ color: COLORS.indigo }} />
            <Typography fontWeight={800} variant="h6" fontFamily={FONT.display}>Cost Sheet: {selectedItem?.salesOrderNo}</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <CostSheetHistory selectedItem={selectedItem} />
            <Button variant="outlined" size="small" startIcon={<PrintIcon />} onClick={() => selectedItem && generateCostSheetPdf(selectedItem, legalVendors)}>Print</Button>
            <IconButton size="small" onClick={handleCloseModal}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3, bgcolor: COLORS.surface }}>
          {selectedItem ? (
            <Box ref={targetRef}>
              <Paper sx={{ p: 2.5, borderRadius: 2, mb: 3, border: `1px solid ${COLORS.border}`, boxShadow: 'none', bgcolor: COLORS.white }}>
                <Grid container spacing={2}>
                  {[
                    { label: 'Date',           val: selectedItem.date ? new Date(selectedItem.date).toLocaleDateString('en-GB') : '—' },
                    { label: 'Delivery Date',  val: selectedItem.deliveryDate ? new Date(selectedItem.deliveryDate).toLocaleDateString('en-GB') : '—' },
                    { label: 'Client Name',    val: selectedItem.clientRegistrations?.[0]?.legalClientName || selectedItem.clientName },
                    { label: 'Created By',     val: selectedItem.createdBy },
                    { label: 'Cost Sheet For', val: selectedItem.costsheetfor },
                    { label: 'Order Status',   val: <OrderStatusChip status={selectedItem.orderStatus} /> },
                    { label: 'Sales SPOC',     val: selectedItem.salesPOC },
                    { label: 'SCM SPOC',       val: selectedItem.scmspoc },
                    { label: 'Payment Terms',  val: selectedItem.clientPaymentTerms },
                    ...(isInternal
                        ? [{ label: 'Internal Category', val: selectedItem.internalCategory }]
                      : [])
                  ].map(({ label, val }, i) => (
                    <Grid item xs={12} sm={6} md={4} key={i}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">{label}</Typography>
                      <Typography variant="body2" fontWeight={700}>{val || '—'}</Typography>
                    </Grid>
                  ))}
                </Grid>
              </Paper>

              <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1.5, fontFamily: FONT.display }}>Order Items</Typography>
              <LineItemsTable
                items={selectedItem.items || []}
                subtotals={{ transportationGST:selectedItem.transportationGST, transportationTotalPp: selectedItem.transportationTotalPp, transportationTotalSp: selectedItem.transportationTotalSp, customizationGST: selectedItem.customizationGST, customizationTotalPp: selectedItem.customizationTotalPp, customizationTotalSp: selectedItem.customizationTotalSp, marketingGST:selectedItem.marketingGST, marketingTotalPp: selectedItem.marketingTotalPp, marketingTotalSp: selectedItem.marketingTotalSp }}
                brands={brands} categories={categories} categorySubcategoryMap={categorySubcategoryMap}
                catalogLoading={catalogLoading}
                viewOnly
                customRows={selectedItem.customRows || []}
                legalVendors={legalVendors}
              />
            </Box>
          ) : <EmptyState msg="No details available." />}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="secondary">Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── EDIT COST SHEET DIALOG ── */}
      <Dialog open={showModal} onClose={() => setShowModal(false)} fullScreen PaperProps={{ sx: { bgcolor: COLORS.surface } }}>
        <DialogTitle sx={{ bgcolor: COLORS.white, borderBottom: `1px solid ${COLORS.border}`, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6" fontWeight={800} fontFamily={FONT.display}>Edit Cost Sheet: {selectedItem?.salesOrderNo}</Typography>
          <IconButton size="small" sx={{ ml: 'auto' }} onClick={() => setShowModal(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {/* Basic Info */}
          <SectionCard sx={{ mb: 2.5 }}>
            <Box sx={{ p: '12px 20px', borderBottom: `1px solid ${COLORS.border}`, borderLeft: `4px solid ${COLORS.indigo}`, background: COLORS.white }}>
              <Typography variant="subtitle2" fontWeight={700} textTransform="uppercase" letterSpacing="0.04em" color={COLORS.indigo}>Basic Information</Typography>
            </Box>
            <Box sx={{ p: '20px 24px', bgcolor: COLORS.white }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Autocomplete
                    options={legalClients}
                    getOptionLabel={(o) => (typeof o === 'string' ? o : (o.legalClientName || ''))}
                    value={
                      legalClients.find(c => c.id === selectedItem.legalClientId)
                      || legalClients.find(c => c.legalClientName === selectedItem.clientName)
                      || null
                    }
                    isOptionEqualToValue={(o, v) => o.id === v?.id}
                    onChange={(_, newVal) => {
                      if (newVal) {
                        handleTopLevelChange('clientName', newVal.legalClientName);
                        handleTopLevelChange('legalClientId', newVal.id);
                        handleTopLevelChange('clientGST', newVal.clientGST || '');
                        handleTopLevelChange('clientState', newVal.clientState || '');
                      } else {
                        handleTopLevelChange('clientName', '');
                        handleTopLevelChange('legalClientId', null);
                        handleTopLevelChange('clientGST', '');
                        handleTopLevelChange('clientState', '');
                      }
                    }}
                    renderOption={(props, option) => (
                      <Box component="li" {...props} key={option.id} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start !important', py: 1 }}>
                        <Typography variant="body2" fontWeight={600} fontSize={13}>{option.legalClientName}</Typography>
                        <Box sx={{ display: 'flex', gap: 1.5, mt: 0.25 }}>
                          {option.clientGST && <Typography variant="caption" sx={{ color: COLORS.textMuted, fontFamily: FONT.mono, fontSize: 11 }}>GST: {option.clientGST}</Typography>}
                          {option.clientState && <Typography variant="caption" sx={{ color: COLORS.textMuted, fontSize: 11 }}>{option.clientState}</Typography>}
                        </Box>
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField {...params} label="Client Name" size="small" required sx={FIELD_SX} />
                    )}
                  />
                  {selectedItem.clientGST && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: COLORS.indigo, fontWeight: 600 }}>
                      GST: {selectedItem.clientGST} {selectedItem.clientState ? `· ${selectedItem.clientState}` : ''}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} md={4}>
                    <FormControl fullWidth size="small" sx={FIELD_SX}>
                    <InputLabel>Cost Sheet For</InputLabel>
                   <Select value={selectedItem.costsheetfor} label="Cost Sheet For" fullWidth size="small"
                                      onChange={(e) => handleTopLevelChange('costsheetfor', e.target.value)} sx={SELECT_SX}>
                                      <MenuItem value="" disabled>Select type…</MenuItem>
                                      <MenuItem value="eyantraventures">Eyantra Ventures</MenuItem>
                                      <MenuItem value="Mozato">Mozato</MenuItem>
                                      <MenuItem value="eYantraIndustries">eYantra Industries</MenuItem>
                                      <MenuItem value="Sample">Sample&nbsp;<Chip label="No SO" size="small" sx={{ height: 16, fontSize: 10, bgcolor: alpha(COLORS.amber, 0.18), color: COLORS.amber, fontWeight: 700 }}/></MenuItem>
                                      <MenuItem value="internal">Internal&nbsp;<Chip label="No SO" size="small" sx={{ height: 16, fontSize: 10, bgcolor: alpha(COLORS.amber, 0.18), color: COLORS.amber, fontWeight: 700 }}/></MenuItem>
                                      <MenuItem value="inventory">Inventory</MenuItem>
                      </Select>
                      </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <UserSelect label="Sales POC" value={selectedItem.salesPOC} onChange={(v) => handleTopLevelChange('salesPOC', v)} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <UserSelect label="SCM SPOC" value={selectedItem.scmspoc} onChange={(v) => handleTopLevelChange('scmspoc', v)} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth size="small" label="Client Payment Terms" value={selectedItem.clientPaymentTerms} onChange={(e) => handleTopLevelChange('clientPaymentTerms', e.target.value)} sx={FIELD_SX} disabled={isInternal}/>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth size="small" label="Location" value={selectedItem.location} onChange={(e) => handleTopLevelChange('location', e.target.value)} sx={FIELD_SX} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small" sx={FIELD_SX}>
                    <InputLabel>Order Status</InputLabel>
                    <Select value={selectedItem.orderStatus} label="Order Status" onChange={(e) => handleTopLevelChange('orderStatus', e.target.value)} sx={SELECT_SX}>
                      {['Pending','On-Hold',"PO's Released to vendors",'Material Inward Done','Purchase Invoice Received','Branding Completed','Material Dispatched','Delivered'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                {isInternal&&(
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small" sx={FIELD_SX}>
                    <InputLabel>Internal Category</InputLabel>
                    <Select value={selectedItem.internalCategory} label="Internal Category" onChange={(e) => handleTopLevelChange('internalCategory', e.target.value)} sx={SELECT_SX}>
                      {INTERNAL_CATEGORY.map(({ value, label }) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                )}
              </Grid>
            </Box>
          </SectionCard>

          {/* Items */}
          <SectionCard>
            <Box sx={{ p: '12px 20px', borderBottom: `1px solid ${COLORS.border}`, borderLeft: `4px solid ${COLORS.success}`, background: COLORS.white }}>
              <Typography variant="subtitle2" fontWeight={700} textTransform="uppercase" letterSpacing="0.04em" color={COLORS.success}>Line Items</Typography>
            </Box>
            <LineItemsTable
              items={selectedItem.items}
              onItemChange={handleEditItemChange}
              onAddItem={addItem}
              onRemoveItem={removeItem}
              isInternal={isInternal}
              subtotals={{ transportationGST:selectedItem.transportationGST, transportationTotalPp: selectedItem.transportationTotalPp, transportationTotalSp: selectedItem.transportationTotalSp,customizationGST: selectedItem.customizationGST, customizationTotalPp: selectedItem.customizationTotalPp, customizationTotalSp: selectedItem.customizationTotalSp, marketingGST: selectedItem.marketingGST, marketingTotalPp: selectedItem.marketingTotalPp, marketingTotalSp: selectedItem.marketingTotalSp }}
              onSubtotalChange={handleTopLevelChange}
              brands={brands} categories={categories} categorySubcategoryMap={categorySubcategoryMap}
              catalogLoading={catalogLoading}
              customVendorTerms={customVendorPaymentTerms}
              onCustomVendorTermChange={(i, v) => setCustomVendorPaymentTerms(prev => ({ ...prev, [i]: v }))}
              qtyErrors={qtyErrors}
              onQtyErrorChange={(i, err) => setQtyErrors(p => ({ ...p, [i]: err }))}
              customRows={selectedItem.customRows || []}
              onCustomRowChange={(updated) =>
                setSelectedItem(prev => ({ ...prev, customRows: updated }))
              }
              onOpenCatalog={(i) => { setCatalogTargetIndex(i); setProductModalOpen(true); }}
              legalVendors={legalVendors}
              onLegalVendorChange={(i, v) => handleEditItemChange(i, 'legalVendorId', v?.id || null)}
            />
          </SectionCard>
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: COLORS.white, borderTop: `1px solid ${COLORS.border}` }}>
          <Button onClick={() => setShowModal(false)} variant="outlined" sx={{ borderRadius: 2, textTransform: 'none', borderColor: COLORS.border }}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained" disabled={!hasChanges() || !selectedItem.salesPOC || !selectedItem.scmspoc || !isEditFormValid()}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, px: 3, bgcolor: COLORS.indigo, '&:hover': { bgcolor: COLORS.navy }, minWidth: 120 }}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm edit modal */}
      <Dialog open={showConfirmModal} onClose={() => setShowConfirmModal(false)} PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle fontWeight={800} fontFamily={FONT.display}>Confirm Changes</DialogTitle>
        <DialogContent><Typography>Are you sure you want to save these changes?</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmModal(false)}>No, Cancel</Button>
          <Button onClick={confirmSaveChanges} variant="contained" autoFocus sx={{ bgcolor: COLORS.indigo }}>Yes, Save</Button>
        </DialogActions>
      </Dialog>

      {/* Deliveries table */}
      <DeliveriesTable ref={tableRef} deliveries={deliveriesToShow} title={tableTitle} color={tableColor} />
      {deliveriesToShow.length > 0 && tableTitle === 'Upcoming Deliveries - Sales Orders' && (
        <Grid container justifyContent="center" spacing={1} sx={{ mt: 1 }}>
          <Grid item><IconButton onClick={handlePrev} disabled={startOffset === 0}><ArrowBackIcon /></IconButton></Grid>
          <Grid item><IconButton onClick={handleNext}><ArrowForwardIcon /></IconButton></Grid>
        </Grid>
      )}

      {/** Product selection modal */}
      <ProductSelectorModal
        open={productModalOpen}
        onClose={() => {
          setProductModalOpen(false);
          setCatalogTargetIndex(null);
        }}
        onSelect={async (product) => {
          if (catalogTargetIndex === null) return;
          const findIn = (list, name) => name ? list.find(i => i.name?.toLowerCase() === name.toLowerCase()) || null : null;
          const ensure = async (type, name, parentId = null) => {
            if (!name) return null;
            try {
              return await sendAuthenticatedRequest(
                `${urlconfig.backend_api_base_url}/api/v2/catalog/ensure-${type}`,
                'POST',
                parentId ? { name, categoryId: parentId } : { name }
              );
            } catch (e) {
              console.error(`Failed to ensure ${type}:`, e);
              return { name };
            }
          };

          let tBrand = product.brandName ? findIn(brands, product.brandName) : null;
          if (product.brandName && !tBrand) {
            tBrand = await ensure('brand', product.brandName);
            if (tBrand) setBrands(p => [...p, tBrand]);
          }

          let tCat = findIn(categories, product.omsCategory);
          if (!tCat && product.omsCategory) {
            tCat = await ensure('category', product.omsCategory);
            if (tCat) {
              setCategories(p => [...p, tCat]);
              setCategorySubcategoryMap(p => ({ ...p, [tCat.id || tCat.name]: [] }));
            }
          }

          let tSub = null;
          if (tCat && product.omsSubcategory) {
            const key = tCat.id || tCat.name;
            tSub = findIn(categorySubcategoryMap[key] || [], product.omsSubcategory);
            if (!tSub) {
              tSub = await ensure('subcategory', product.omsSubcategory, tCat.id);
              if (tSub) setCategorySubcategoryMap(p => ({ ...p, [key]: [...(p[key] || []), tSub] }));
            }
          }

          setSelectedItem(prev => ({
            ...prev,
            items: prev.items.map((item, idx) => {
              if (idx !== catalogTargetIndex) return item;
              const u = {
                ...item,
                details: product.productName || item.details,
                esin: product.ecomProductId?.toString() || item.esin || '',
                ecomProductName: product.productName || item.ecomProductName || '',
                vendorName: product.lastVendor || item.vendorName,
                pp: (product.ecomCostPrice ?? '').toString(),
                sp: (product.ecomMrp ?? '').toString(),
                brand: tBrand || item.brand,
                category: tCat || item.category,
                subcategory: tSub || item.subcategory,
                gst: (product.ecomGst ?? '').toString(),
                qty: '',
              };
              const pp = parseFloat(u.pp) || 0;
              const sp = parseFloat(u.sp) || 0;
              const gst = parseFloat(u.gst) || 0;
              const qty = parseInt(u.qty) || 0;
              const nlc = pp * (1 + gst / 100);
              const nsp = sp * (1 + gst / 100);
              const tPP = pp * qty, tSP = sp * qty, mg = tSP - tPP;
              return {
                ...u,
                nlc: nlc.toFixed(2), nsp: nsp.toFixed(2),
                totalPp: tPP.toFixed(2), totalSp: tSP.toFixed(2),
                margin: mg.toFixed(2),
                marginPercent: tPP !== 0 ? ((mg / tPP) * 100).toFixed(1) : '0.0',
                totPPGST: (nlc * qty).toFixed(2),
                totSPGST: (nsp * qty).toFixed(2),
              };
            }),
          }));

          setProductModalOpen(false);
          setCatalogTargetIndex(null);
        }}
        baseUrl={urlconfig.backend_api_base_url}
      />

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast('')} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setToast('')} severity="success" variant="filled" sx={{ width: '100%' }}>{toast}</Alert>
      </Snackbar>
    </PageShell>
  );
};

export default UserDashboard;